// Phone photos are often 4000px+ and 3-6MB. Sharetribe bills listing photo uploads by the
// gigabyte, and the biggest size the site ever shows is 2400px (scaled-xlarge), so photos are
// scaled down in the browser before upload. Anything that can't be read here (e.g. HEIC in
// Chrome) uploads unchanged, exactly as before.
export const MAX_UPLOAD_DIMENSION = 2400;
const MIN_BYTES_TO_SHRINK = 1024 * 1024;
const JPEG_QUALITY = 0.9;
const SHRINKABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

const canvasToJpegBlob = canvas =>
  new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));

export const shrinkImageForUpload = async file => {
  const canShrink =
    typeof window !== 'undefined' &&
    typeof window.createImageBitmap === 'function' &&
    file &&
    SHRINKABLE_TYPES.includes(file.type) &&
    file.size >= MIN_BYTES_TO_SHRINK;
  if (!canShrink) {
    return file;
  }

  let bitmap;
  try {
    // 'from-image' applies the camera's rotation, so the pixels come out upright.
    bitmap = await window.createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // JPEG has no transparency: give see-through PNG areas a white background instead of black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvasToJpegBlob(canvas);
    if (!blob || blob.size >= file.size) {
      return file;
    }
    const name = (file.name || 'photo').replace(/\.[^/.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (e) {
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === 'function') {
      bitmap.close();
    }
  }
};
